import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import AiChatService from '../Services/ai-chat/AiChat';
import { aiChatKeys } from '../queries/useGetChat';

const getChatErrorMessage = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const code = data?.error || data?.code;

  if (status === 401) return 'Votre session a expire. Veuillez vous reconnecter.';
  if (status === 403 && code === 'subscription_required') return 'Un abonnement actif est requis pour utiliser le chat IA.';
  if (status === 403) return data?.message || 'Vous n avez pas acces au chat IA.';
  if (status === 404) return 'Conversation ou professeur introuvable. Actualisez la page.';
  if (status === 413) return 'Votre message est trop long. Raccourcissez-le puis reessayez.';
  if (status === 429) {
    const retryAfter = data?.retry_after_seconds;
    return retryAfter
      ? `Limite atteinte. Reessayez dans ${retryAfter} secondes.`
      : 'Limite atteinte. Veuillez reessayer dans un instant.';
  }

  return data?.message || error?.message || 'Erreur lors de la communication avec le chat IA.';
};

export const useChatMutations = () => {
  const queryClient = useQueryClient();

  const createSessionMutation = useMutation({
    mutationFn: AiChatService.createSession,
    onSuccess: (session) => {
      if (session?.teacher_id) {
        queryClient.invalidateQueries({ queryKey: aiChatKeys.teacherSessions(session.teacher_id) });
      }
    },
    onError: (error) => {
      toast.error(getChatErrorMessage(error));
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: ({ sessionId }) => AiChatService.deleteSession(sessionId),
    onSuccess: (_response, variables) => {
      if (variables?.teacherId) {
        queryClient.invalidateQueries({ queryKey: aiChatKeys.teacherSessions(variables.teacherId) });
      }
      if (variables?.sessionId) {
        queryClient.removeQueries({ queryKey: aiChatKeys.session(variables.sessionId) });
      }
      toast.success('Conversation supprimee avec succes');
    },
    onError: (error) => {
      toast.error(getChatErrorMessage(error));
    },
  });

  const sendChatMessageMutation = useMutation({
    mutationFn: async ({ sessionId, message, onChunk, onDone }) => {
      return AiChatService.streamChatMessage(sessionId, message, { onChunk, onDone });
    },
    onSuccess: (_payload, variables) => {
      if (variables?.sessionId) {
        queryClient.invalidateQueries({ queryKey: aiChatKeys.session(variables.sessionId) });
        queryClient.invalidateQueries({ queryKey: aiChatKeys.sessionTokens(variables.sessionId) });
      }
      if (variables?.teacherId) {
        queryClient.invalidateQueries({ queryKey: aiChatKeys.teacherSessions(variables.teacherId) });
      }
    },
    onError: (error, variables) => {
      variables?.onError?.(error);
      toast.error(getChatErrorMessage(error));
    },
  });

  return {
    createSessionMutation,
    deleteSessionMutation,
    sendChatMessageMutation,
  };
};
